import { useState, useLayoutEffect, useContext, useMemo } from 'react';
import { Action } from 'redux';
import {
  Actions,
  Dispatchers,
  ReactReduxHook,
  Reducers,
  RestParams,
  PayloadAction
} from './types';
import { ModularReduxContext } from '../helpers';
import { StoreWithInjectReducer } from './configureStore';

const subscribe = (
  store: StoreWithInjectReducer<Record<string, any>>,
  storeKey: string,
  callback: (state) => void
) => {
  let lastState = store.getState()[storeKey];
  if (store) {
    return store.subscribe(
      // eslint-disable-next-line no-return-assign
      () =>
        lastState !== store.getState()[storeKey] &&
        callback((lastState = store.getState()[storeKey]))
    );
  }
  return () => {
    // returns an empty unsubscribe function if store unavailable
  };
};

export const useRedux = <TState, TReducers extends Reducers<TState>>(
  storeKey: string,
  sliceReducers: TReducers,
  initialState: TState
): [
  ReactReduxHook<TState>,
  ReactReduxHook<Dispatchers<TReducers>>,
  Actions<any>
] => {
  const useSlice = () => {
    const store: StoreWithInjectReducer<Record<string, any>> =
      useContext(ModularReduxContext);

    const [state, setState] = useState(store.getState()[storeKey]);

    useLayoutEffect(() => subscribe(store, storeKey, setState), [setState]);

    return state;
  };

  const dispatcherNames = Object.keys(
    sliceReducers
  ) as (keyof typeof sliceReducers)[];

  const useDispatchers = () => {
    const store: StoreWithInjectReducer<Record<string, any>> =
      useContext(ModularReduxContext);

    if (!store.getState()[storeKey]) {
      store.injectReducer(
        storeKey,
        preloadedState =>
          (state = preloadedState || initialState, { type, payload }) =>
            sliceReducers[type]
              ? sliceReducers[type](state as TState, payload)
              : state
      );
    }

    const dispatchers: Dispatchers<TReducers> = dispatcherNames.reduce(
      (prev, dispatcherName) => ({
        ...prev,
        [dispatcherName]: (
          payload: RestParams<typeof dispatchers[typeof dispatcherName]>
        ) => {
          return store.dispatch({ type: dispatcherName, payload });
        }
      }),
      {} as Dispatchers<TReducers>
    );
    return dispatchers;
  };

  const actions: Actions<any> = dispatcherNames.reduce(
    (prev, actionName) => ({
      ...prev,
      [actionName]: (
        payload: RestParams<typeof actions[typeof actionName]>
      ) => ({ type: actionName, payload })
    }),
    {} as Actions<any>
  );

  return [useSlice, useDispatchers, actions];
};
